import { type ReactNode, useEffect, useRef, useState } from "react";
import { authClient, isNonRetryableRefreshError, refreshAccessToken } from "@/api/auth-client";
import { getTokenExpiresIn } from "@/utils/jwt-utils";
import { useNavigate, useLocation } from "react-router";
import { useAppDispatch, useAppSelector } from "@/store";
import { clearCredentials, markResolved, selectAccessToken, selectIsResolved, setCredentials } from "@/store/auth.slice";
import { LoadingState, LOADING_STATE_VARIANT } from "@/components/base/loading-state/loading-state";
import { saveAuthRedirect } from "@/utils/auth-redirect";

/** Routes that never require authentication */
const PUBLIC_PATHS = ["/login", "/otc", "/sso/callback"];
const TOKEN_REFRESH_THRESHOLD_SECONDS = 60;

const isTokenValid = (token: string | null | undefined): token is string => !!token && !authClient.isTokenExpired(token);

const shouldPreemptivelyRefresh = (token: string | null | undefined): token is string => {
    if (!token) {
        return false;
    }

    try {
        return getTokenExpiresIn(token) <= TOKEN_REFRESH_THRESHOLD_SECONDS;
    } catch {
        return false;
    }
};

/**
 * AuthGuard — verifies the current route against the access token.
 *
 * Flow:
 *  1. Public path (/login, /otc)  → mark resolved, render immediately
 *  2. Missing or expired access token → try refresh, then redirect only if still unauthenticated
 *  3. Valid access token              → render protected content
 *
 * Renders a spinner while the check is in flight so children never
 * render with an empty auth state.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isResolved = useAppSelector(selectIsResolved);
    const accessToken = useAppSelector(selectAccessToken);
    const [isChecking, setIsChecking] = useState(false);
    const runIdRef = useRef(0);

    useEffect(() => {
        let cancelled = false;
        const runId = ++runIdRef.current;
        const isCurrentRun = () => !cancelled && runId === runIdRef.current;
        const setCheckingIfCurrent = (value: boolean) => {
            if (isCurrentRun()) {
                setIsChecking(value);
            }
        };
        const resolveIfNeeded = () => {
            if (!isResolved) {
                dispatch(markResolved());
            }
        };

        const verifyAuth = async () => {
            const token = authClient.getAccessToken() ?? accessToken;
            const hasValidToken = isTokenValid(token);
            const isPublic = PUBLIC_PATHS.some((p) => location.pathname.startsWith(p));

            if (isPublic) {
                resolveIfNeeded();
                setCheckingIfCurrent(false);

                if (location.pathname.startsWith("/login") && hasValidToken) {
                    navigate("/", { replace: true });
                }
                return;
            }

            if (hasValidToken && shouldPreemptivelyRefresh(token)) {
                try {
                    const refreshed = await refreshAccessToken();
                    dispatch(setCredentials({ accessToken: refreshed.accessToken }));
                } catch (error) {
                    if (isNonRetryableRefreshError(error) && authClient.isTokenExpired(token)) {
                        dispatch(clearCredentials());
                        setCheckingIfCurrent(false);
                        saveAuthRedirect(`${location.pathname}${location.search}${location.hash}`);
                        navigate("/login", { replace: true });
                        return;
                    }
                    // Keep the current token when it is still valid and refresh failed transiently.
                }

                if (!isCurrentRun()) {
                    return;
                }

                resolveIfNeeded();
                return;
            }

            if (!hasValidToken) {
                setCheckingIfCurrent(true);
                try {
                    const refreshed = await refreshAccessToken();
                    dispatch(setCredentials({ accessToken: refreshed.accessToken }));
                    if (!isCurrentRun()) {
                        return;
                    }

                    setCheckingIfCurrent(false);
                    return;
                } catch (error) {
                    if (!isCurrentRun()) {
                        return;
                    }

                    const latestToken = authClient.getAccessToken();
                    if (isTokenValid(latestToken)) {
                        dispatch(setCredentials({ accessToken: latestToken }));
                        setCheckingIfCurrent(false);
                        return;
                    }

                    if (!isNonRetryableRefreshError(error)) {
                        try {
                            const retried = await refreshAccessToken();
                            dispatch(setCredentials({ accessToken: retried.accessToken }));
                            if (!isCurrentRun()) {
                                return;
                            }
                            setCheckingIfCurrent(false);
                            return;
                        } catch {
                            // fall through to logout path
                        }
                    }

                    dispatch(clearCredentials());
                    setCheckingIfCurrent(false);
                    saveAuthRedirect(`${location.pathname}${location.search}${location.hash}`);
                    navigate("/login", { replace: true });
                    return;
                }
            }

            setCheckingIfCurrent(false);
            resolveIfNeeded();
        };

        void verifyAuth();

        return () => {
            cancelled = true;
        };
    }, [accessToken, dispatch, isResolved, location.pathname, navigate]);

    if (!isResolved || isChecking) {
        return <LoadingState variant={LOADING_STATE_VARIANT.Block} label="Checking authentication" className="min-h-screen bg-gray-50" />;
    }

    return children;
}
